import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Building2, FileDown, Sheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, openBlob } from "@/lib/api";
import FacilityDrillDown from "@/components/FacilityDrillDown";
import { usePractice } from "@/pages/StaffApp";

const COLS = [["signed", "Signed", "bg-emerald-500", "text-emerald-700"], ["pending", "Pending", "bg-sky-500", "text-sky-700"],
  ["declined", "Declined", "bg-rose-500", "text-rose-700"], ["expired", "Expired / voided", "bg-slate-400", "text-slate-600"]];

function Bar({ r }) {
  return (
    <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 w-full">
      {COLS.map(([k, , bg]) => r[k] > 0 && <div key={k} className={bg} style={{ width: `${(100 * r[k]) / r.total}%` }} />)}
    </div>
  );
}

function GroupTable({ title, icon: Icon, rows, testid, onPick }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid={testid}>
      <p className="px-4 py-3 border-b font-semibold flex items-center gap-2"><Icon className="w-4 h-4 text-indigo-600" />{title} <span className="text-xs text-slate-500 font-normal">({rows.length})</span></p>
      <div className="px-4 py-1.5 bg-slate-50 border-b flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500"><span className="flex-1">Name</span>
        {COLS.map(([k, l, , tc]) => <span key={k} className={tc}>{l.split(" ")[0]}</span>)}<span className="w-14 text-right whitespace-nowrap">Signed %</span></div>
      <div className="divide-y max-h-[420px] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.code} className={`px-4 py-2.5 text-sm ${onPick ? "cursor-pointer hover:bg-indigo-50/60" : ""}`} onClick={() => onPick?.(r)} data-testid={`${testid}-row-${r.code}`}>
            <div className="flex items-center gap-2"><span className="font-medium flex-1 truncate">{r.name}</span>
              {COLS.map(([k, l, , tc]) => <span key={k} className={`text-xs tabular-nums ${tc}`} title={l}>{r[k]}</span>)}
              <span className="text-xs font-bold w-14 text-right" data-testid={`${testid}-rate-${r.code}`}>{r.signed_rate}%</span></div>
            <div className="mt-1.5"><Bar r={r} /></div>
          </div>
        ))}
        {!rows.length && <p className="p-4 text-sm text-slate-500">No packets in this period.</p>}
      </div>
    </div>
  );
}

export default function GroupDashboard() {
  const { scope, practice } = usePractice();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [drill, setDrill] = useState(null);
  const dl = (ext) => openBlob(`/reports/group.${ext}?days=${days}`, `group-dashboard.${ext}`).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/reports/group", { params: { days } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [days, practice.id]);
  return (
    <div className="mt-8 min-w-0" data-testid="group-dashboard">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-xl font-bold">{scope?.limited ? "My group" : "All providers & facilities"}</h2>
        <div className="flex flex-wrap gap-1 sm:ml-auto">{[7, 30, 90].map((n) => (
          <Button key={n} size="sm" variant={days === n ? "default" : "outline"} onClick={() => setDays(n)} data-testid={`group-days-${n}`}>{n} days</Button>))}
          <Button size="sm" variant="outline" className="ml-2" onClick={() => dl("pdf")} data-testid="group-export-pdf"><FileDown className="w-4 h-4 mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={() => dl("xlsx")} data-testid="group-export-xlsx"><Sheet className="w-4 h-4 mr-1" />Excel</Button></div>
      </div>
      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">{COLS.map(([k, l, bg]) => (
            <div key={k} className="bg-white border rounded-xl p-4 flex items-center gap-3"><span className={`w-2 h-10 rounded-full ${bg}`} />
              <div><p className="text-xs uppercase tracking-wider text-slate-500">{l}</p><p className="text-2xl font-bold tabular-nums" data-testid={`group-total-${k}`}>{d.totals[k]}</p></div></div>))}</div>
          <div className="grid lg:grid-cols-2 gap-4">
            <GroupTable title="By provider" icon={Users} rows={d.providers} testid="group-providers" />
            <GroupTable title="By facility (click to follow up)" icon={Building2} rows={d.facilities} testid="group-facilities" onPick={(r) => r.code !== "none" && setDrill(r)} />
          </div>
        </>
      )}
      <FacilityDrillDown fac={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
